import { IObservableArray, makeAutoObservable, observable } from "mobx";
import { keyBy } from "lodash";
import { AppStore } from "store/App.store";
import { AccountLeadIconsApi } from "api/account-lead-icons.api";
import { TLeadIconConfig } from "types/lead-icon.type";
import { LeadIcon } from "enums/lead-icon.enum";
import { getLeadIconKey } from "constants/lead-icons";

const accountLeadIconsApi = new AccountLeadIconsApi();

export class AccountLeadIconsStore {
  private readonly root: AppStore;
  private readonly accountId: number;
  private leadIconConfigs: IObservableArray<TLeadIconConfig>;

  constructor(root: AppStore, accountId: number) {
    makeAutoObservable(this, {}, { autoBind: true });

    this.root = root;
    this.accountId = accountId;
    this.leadIconConfigs = observable.array<TLeadIconConfig>();
  }

  get leadIconConfigsArray() {
    return this.leadIconConfigs.slice();
  }

  get leadIconConfigMap(): Record<string, TLeadIconConfig> {
    return keyBy(this.leadIconConfigsArray, config =>
      getLeadIconKey(config.source, config.type)
    );
  }

  private setLeadIconConfigs(configs: TLeadIconConfig[]) {
    this.leadIconConfigs.replace(configs);
  }

  private indexOfLeadIcon(id: number) {
    return this.leadIconConfigsArray.findIndex(config => config.id === id);
  }

  private upsertLeadIconConfig(config: TLeadIconConfig) {
    const index = this.indexOfLeadIcon(config.id);
    if (index !== -1) {
      this.leadIconConfigs[index] = config;
    } else {
      this.leadIconConfigs.push(config);
    }
  }

  private removeLeadIconConfig(id: number) {
    const index = this.indexOfLeadIcon(id);
    if (index !== -1) {
      this.leadIconConfigs.splice(index, 1);
    }
  }

  public async fetchAccountLeadIcons() {
    const configs = await accountLeadIconsApi.fetchAccountLeadIcons(
      this.accountId
    );
    this.setLeadIconConfigs(configs);
  }

  public async createLeadIcon(source: string, type: string, icon: LeadIcon) {
    const created = await accountLeadIconsApi.createAccountLeadIcon(
      this.accountId,
      { source, type, icon }
    );
    this.upsertLeadIconConfig(created);
  }

  public async updateLeadIcon(leadIconId: number, icon: LeadIcon) {
    const updated = await accountLeadIconsApi.updateAccountLeadIcon(
      this.accountId,
      leadIconId,
      { icon }
    );
    this.upsertLeadIconConfig(updated);
  }

  public async deleteLeadIcon(leadIconId: number) {
    await accountLeadIconsApi.deleteAccountLeadIcon(this.accountId, leadIconId);
    this.removeLeadIconConfig(leadIconId);
  }
}
